import { useEffect, useMemo, useRef, useState } from 'react'
import {
  ETypeOfNotification,
  Modal,
  RColumn,
  RemoteTable,
  RemoteTableRef,
  setNotification,
  stringFilterTemplate,
} from 'medere-ui-framework'
import { Button } from 'primereact/button'
import { MultiSelect, MultiSelectChangeEvent } from 'primereact/multiselect'
import { useDispatch } from 'react-redux'
import { KitListItemDTO, KitsListQueryParams } from '../../dto/kits/KitsDTO'
import { deleteKit, getKitsList } from '../../services/KitsController'
import LocationActionMenu, { LocationActionItem } from '../Location/tabs/components/LocationActionMenu'
import KitsListCard, { KitsCardActionsProps } from './components/KitsListCard'
import KitsFormView from './KitsFormView'
import './Kits.scss'

type UnknownRecord = Record<string, unknown>

interface FilterOption<TValue> {
  label: string
  value: TValue
}

const MOBILE_SEARCH_BATCH_SIZE = 200
const MOBILE_SEARCH_MAX_SCAN = 2000
const MOBILE_FILTER_DEBOUNCE_MS = 300

function isRecord(value: unknown): value is UnknownRecord {
  return typeof value === 'object' && value !== null
}

function normalizeFilterText(value: string | undefined | null) {
  return String(value ?? '')
    .normalize('NFD')
    .replace(/[\u0300-\u036f]/g, '')
    .toLowerCase()
    .trim()
}

function matchesMobileFilter(item: KitListItemDTO, normalizedFilterText: string) {
  if (normalizedFilterText === '') return true

  const searchableValues = [
    item.description,
    item.stockItemsKitTypeName,
    item.active ? 'activo' : 'inactivo',
  ]

  return searchableValues.some((value) => normalizeFilterText(value).includes(normalizedFilterText))
}

export default function KitsView() {
  const dispatch = useDispatch()
  const tableRef = useRef<RemoteTableRef>(null)
  const [selectedKitId, setSelectedKitId] = useState<number | null>(null)
  const [activeFilter, setActiveFilter] = useState<boolean[]>([])
  const [mobileFilterDraft, setMobileFilterDraft] = useState('')
  const [mobileFilterText, setMobileFilterText] = useState('')
  const [deleteModal, setDeleteModal] = useState(false)
  const [kitToDelete, setKitToDelete] = useState<KitListItemDTO | null>(null)

  const activeOptions: Array<FilterOption<boolean>> = [
    { label: 'Activo', value: true },
    { label: 'Inactivo', value: false },
  ]

  function clearTableFiltersStorage() {
    const tableStateKeys = ['KitsTable', 'mobileFilter_KitsTable']
    tableStateKeys.forEach((key) => {
      localStorage.removeItem(key)
      sessionStorage.removeItem(key)
    })
  }

  useEffect(() => {
    clearTableFiltersStorage()
    return () => {
      clearTableFiltersStorage()
    }
  }, [])

  useEffect(() => {
    tableRef.current?.refreshTable?.()
  }, [activeFilter, mobileFilterText])

  useEffect(() => {
    const timeoutId = window.setTimeout(() => {
      setMobileFilterText(mobileFilterDraft)
    }, MOBILE_FILTER_DEBOUNCE_MS)

    return () => {
      window.clearTimeout(timeoutId)
    }
  }, [mobileFilterDraft])

  async function loadKits(offSet: number, pageSize: number, params: KitsListQueryParams = {}) {
    const normalizedMobileFilterText = normalizeFilterText(mobileFilterText)
    const currentActiveFilter = activeFilter.length > 0 ? activeFilter : []
    const hasExtraFilters = normalizedMobileFilterText !== '' || currentActiveFilter.length > 0

    if (!hasExtraFilters) {
      return getKitsList(offSet, pageSize, params)
    }

    const fetchSize = Math.max(MOBILE_SEARCH_BATCH_SIZE, pageSize)
    let fetchOffset = 0
    let scannedItems = 0
    const requiredMatches = offSet + pageSize
    const filteredItems: KitListItemDTO[] = []

    while (scannedItems < MOBILE_SEARCH_MAX_SCAN && filteredItems.length < requiredMatches) {
      const batch = await getKitsList(fetchOffset, fetchSize, {
        ...params,
        cardFilterDTO: null,
      })

      if (batch.length === 0) {
        break
      }

      scannedItems += batch.length
      filteredItems.push(...batch.filter((item) => {
        const matchesStatus = currentActiveFilter.length === 0 || currentActiveFilter.includes(item.active)
        return matchesStatus && matchesMobileFilter(item, normalizedMobileFilterText)
      }))

      if (batch.length < fetchSize) {
        break
      }

      fetchOffset += fetchSize
    }

    return filteredItems.slice(offSet, offSet + pageSize)
  }

  function openEdit(item: KitListItemDTO) {
    setSelectedKitId(item.kitId)
  }

  function openDelete(item: KitListItemDTO) {
    setKitToDelete(item)
    setDeleteModal(true)
  }

  async function confirmDelete() {
    if (!kitToDelete) {
      setDeleteModal(false)
      return
    }

    try {
      await deleteKit(kitToDelete.kitId)
      dispatch(setNotification({
        title: 'Kit eliminado',
        desc: 'El kit se eliminó correctamente.',
        typeOfNotification: ETypeOfNotification.INFO,
        icon: 'pi pi-check',
      }))
      tableRef.current?.refreshTable?.()
    } catch (error: unknown) {
      const response = isRecord(error) && isRecord(error.response) ? error.response : null
      const status = typeof response?.status === 'number' ? response.status : null

      dispatch(setNotification({
        title: status === 409 ? 'No se puede eliminar' : 'Error',
        desc: status === 409
          ? 'El kit está siendo utilizado en otras relaciones.'
          : 'No se pudo eliminar el kit.',
        typeOfNotification: status === 409 ? ETypeOfNotification.WARNING : ETypeOfNotification.ERROR,
        icon: status === 409 ? 'pi pi-exclamation-triangle' : 'pi pi-times',
      }))
    } finally {
      setDeleteModal(false)
      setKitToDelete(null)
    }
  }

  function getFilterText(input: unknown): string {
    if (typeof input === 'string') return input
    if (!isRecord(input)) return ''
    const target = isRecord(input.target) ? input.target : null
    return typeof target?.value === 'string' ? target.value : ''
  }

  function handleMobileFilterChange(input: unknown) {
    setMobileFilterDraft(getFilterText(input))
  }

  const handleClearFilters = useMemo(() => () => {
    setActiveFilter([])
  }, [])

  function handleTableClearFiltersClick(event: React.MouseEvent<HTMLDivElement>) {
    const target = event.target
    if (!(target instanceof HTMLElement)) return

    const clearButton = target.closest('button[aria-label="Limpiar filtros"]')
    if (!clearButton) return

    clearTableFiltersStorage()
    window.setTimeout(() => {
      handleClearFilters()
      setMobileFilterDraft('')
      setMobileFilterText('')
    }, 0)
  }

  const cardActionsProps = useMemo<KitsCardActionsProps>(() => ({
    onEdit: openEdit,
    onDelete: openDelete,
  }), [])

  const rowActions = useMemo(() => (rowData: KitListItemDTO) => {
    const menuItems: LocationActionItem[] = [
      {
        label: 'Editar',
        icon: 'pi pi-file-edit',
        action: () => openEdit(rowData),
      },
      {
        label: 'Eliminar',
        icon: 'pi pi-trash',
        action: () => openDelete(rowData),
      },
    ]

    return (
      <LocationActionMenu
        items={menuItems}
        ariaLabel='Opciones del kit'
        buttonId={`kitsMenu_${rowData.kitId}`}
        buttonClassName='p-button-text'
        panelClassName='kits-action-menu-panel'
      />
    )
  }, [])

  const tableParams = useMemo(() => ({
    activeFilter: activeFilter.length > 0 ? activeFilter : [],
  }), [activeFilter])

  const tableRenderKey = useMemo(
    () => `kits-table-${activeFilter.join('|')}-${mobileFilterText}`,
    [activeFilter, mobileFilterText],
  )

  const activeFilterElement = useMemo(() => () => (
    <MultiSelect
      options={activeOptions}
      optionLabel='label'
      optionValue='value'
      value={activeFilter}
      onChange={(e: MultiSelectChangeEvent) => setActiveFilter((e.value ?? []) as boolean[])}
      className='w-full kits-filter-multiselect'
      style={{ height: '2.25rem', marginBottom: '0.28rem' }}
    />
  ), [activeFilter])

  if (selectedKitId != null) {
    return (
      <KitsFormView
        kitId={selectedKitId}
        setKitId={setSelectedKitId}
        onSaved={() => tableRef.current?.refreshTable?.()}
      />
    )
  }

  return (
    <div className='viewContainer kits-view' onClick={handleTableClearFiltersClick}>
      <div className='kits-header'>
        <h1 className='kits-title'>Kits</h1>
        <Button
          id='buttonCreateKitId'
          type='button'
          className='buttonMain'
          label='Crear'
          icon='pi pi-plus-circle'
          onClick={() => {
            setSelectedKitId(-1)
          }}
        />
      </div>
      <div className='kits-divider' />

      <RemoteTable
        key={tableRenderKey}
        idTable='KitsTable'
        dataKey='kitId'
        className='kits-table'
        getItems={loadKits}
        resizableColumns
        dynamicBehaviour
        CardTemplate={KitsListCard}
        cardKeyName='kitId'
        cardAdditionalProps={cardActionsProps}
        handleMobileFilter={handleMobileFilterChange}
        params={tableParams}
        ref={tableRef}
        filterRow
        rowActions={rowActions}
      >
        <RColumn field='description' className='column' header='Descripción' filter filterElement={stringFilterTemplate} />
        <RColumn
          field='stockItemsKitTypeName'
          className='column'
          header='Tipo de kit'
          filter
          filterElement={stringFilterTemplate}
        />
        <RColumn
          field='active'
          className='column'
          header='Estado'
          filter
          filterElement={activeFilterElement}
          body={(item: KitListItemDTO) => (item.active ? 'Activo' : 'Inactivo')}
        />
      </RemoteTable>

      <Modal
        visible={deleteModal}
        setVisible={setDeleteModal}
        header='Eliminar kit'
        footerButtonRightText='Eliminar'
        footerButtonLeftText='Cancelar'
        onClickRightBtn={() => { void confirmDelete() }}
        onClickLeftBtn={() => setDeleteModal(false)}
      >
        <p>
          ¿Querés eliminar el kit <strong>{kitToDelete?.description ?? ''}</strong>?
        </p>
      </Modal>
    </div>
  )
}
